import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Mail, MailPlus, Lock } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { NoteCard } from "@/components/NoteCard";
import { MessageDialog } from "@/components/MessageDialog";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Petals } from "@/components/Petals";

export default function Member() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    api.get(`/members/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  if (data === null) {
    return <div className="garden-bg flex justify-center pt-32"><Loader2 className="w-8 h-8 text-pink-400 animate-spin" /></div>;
  }
  if (data === false) {
    return <div className="garden-bg text-center pt-32 text-slate-400">Member not found.</div>;
  }

  const m = data.member;
  const isPublic = m.privacy === "public";

  const handleMessage = () => (user ? setMsgOpen(true) : setAuthOpen(true));

  return (
    <div className="garden-bg">
      <Petals count={6} />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 pt-14 pb-10">
        <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 mb-8">
          <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-pink-500/40 bg-pink-500/10 flex items-center justify-center">
            {m.picture ? <img src={fileUrl(m.picture)} alt={m.name} className="w-full h-full object-cover" />
              : <span className="font-serif text-4xl text-pink-300">{m.name?.[0]?.toUpperCase() || "?"}</span>}
          </div>
          <div className="text-center sm:text-left flex-1">
            <h1 className="font-serif text-4xl font-bold text-pink-50 text-glow" data-testid="member-name">{m.name}</h1>
            {m.bio && <p className="text-slate-300 mt-1">{m.bio}</p>}
            <div className="mt-3 flex items-center justify-center sm:justify-start gap-3 flex-wrap">
              {isPublic && m.contact_email ? (
                <a href={`mailto:${m.contact_email}`} data-testid="member-email-link" className="flex items-center gap-2 text-sm text-pink-200 bg-pink-500/15 px-4 py-2 rounded-full border border-pink-500/25 hover:bg-pink-500/25 transition-all">
                  <Mail className="w-4 h-4" /> {m.contact_email}
                </a>
              ) : (
                <span className="flex items-center gap-2 text-sm text-slate-400 bg-white/5 px-4 py-2 rounded-full border border-pink-500/15">
                  <Lock className="w-4 h-4" /> Private member
                </span>
              )}
              <button
                data-testid="member-message-btn"
                onClick={handleMessage}
                className="flex items-center gap-2 text-sm bg-gradient-to-r from-pink-600 to-rose-500 text-white px-4 py-2 rounded-full hover:scale-105 transition-all shadow-lg shadow-pink-500/25"
              >
                <MailPlus className="w-4 h-4" /> Send a message
              </button>
            </div>
          </div>
        </div>

        <Tabs defaultValue="received">
          <TabsList className="bg-black/40 border border-pink-500/20">
            <TabsTrigger value="received" data-testid="member-tab-received" className="data-[state=active]:bg-pink-500/20 data-[state=active]:text-pink-200">
              Notes Received ({data.notes_received.length})
            </TabsTrigger>
            <TabsTrigger value="left" data-testid="member-tab-left" className="data-[state=active]:bg-pink-500/20 data-[state=active]:text-pink-200">
              Notes Left ({data.notes_left.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="received" className="mt-6">
            {data.notes_received.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.notes_received.map((n, i) => <NoteCard key={n.id} note={n} index={i} />)}
              </div>
            ) : <p className="text-slate-400 text-sm">No notes received yet.</p>}
          </TabsContent>
          <TabsContent value="left" className="mt-6">
            {data.notes_left.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.notes_left.map((n, i) => <NoteCard key={n.id} note={n} index={i} />)}
              </div>
            ) : <p className="text-slate-400 text-sm">No notes left yet.</p>}
          </TabsContent>
        </Tabs>
      </section>

      <MessageDialog open={msgOpen} onOpenChange={setMsgOpen} member={m} />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => setMsgOpen(true)} />
    </div>
  );
}
